import {
	CURRENCIES,
	DATE_PARAM_NAMES,
	DEFAULT_SORTER,
	defaultTimeZone,
	emailRegex,
	FILTER_CONFIG_NAMES,
	hydratedPropNames,
	PAGINATION_PROPS,
	PAGINATION_PROPS_WITHOUT_PAGESIZE,
	ROLE_TYPES,
	TAB_PROPS,
} from './DataConstants';
import {
	NOTE_TYPE_LOG,
	NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../constants/NoteTypes';
import JSONCrush from 'jsoncrush';
import moment from 'moment-timezone';
import { BUYER_ADMIN_ONLY, SUPPLIER_ADMIN_ONLY } from '../components/roles/roles';
import { capitalizeEachWord, wallTimeToUtcIso } from './DataFormatterUtils';
import { isInternalTech } from './AuthUtils';

export function getObjectValues(obj: any): any[] {
	if (obj === null || obj === undefined) {
		return [];
	}
	return Object.keys(obj).map((k) => obj[k]);
}

// Drops keys whose values are '', null, or undefined. Preserves 0, false,
// empty arrays, and empty objects since those are meaningful domain values.
// Use for shaping bulk-upload row payloads where omitted keys mean
// "field not provided" on the server.
export function omitEmptyKeys<T extends Record<string, any>>(obj: T): Partial<T> {
	const result: Partial<T> = {};
	Object.keys(obj).forEach((key) => {
		const value = obj[key];
		if (value !== '' && value !== null && value !== undefined) {
			(result as Record<string, any>)[key] = value;
		}
	});
	return result;
}

export function nullSafeGetOrElse(path: string, obj: any, elseVal: any): any {
	const accessKeys = path.split('.');
	return accessKeys.reduce((xs, x) => (xs && xs[x] ? xs[x] : elseVal), obj);
}

export function nullSafeGet(path: string, obj: any): any {
	const accessKeys = path.split('.');
	return accessKeys.reduce((xs, x) => (xs && xs[x] ? xs[x] : undefined), obj);
}

/**
 * Normalizes whatever a thunk rejected with into something safe to render.
 *
 * Rejection shapes vary across the thunk layer: the REST helpers reject with the backend's
 * `message` string (this is how a 409 conflict's copy reaches the UI), `FetchUtils.request`
 * rejects with a `{ message }` object in read-only mode, and a genuine network failure rejects
 * with an `Error`. Passing any of the latter straight to `message.error` renders "[object Object]".
 */
export const getDisplayableErrorMessage = (error: any, fallback: string): string => {
	if (typeof error === 'string') {
		return error.trim() || fallback;
	}
	const errorMessage = error && error.message;
	return typeof errorMessage === 'string' && errorMessage.trim() ? errorMessage : fallback;
};

export const safeJSONParseOrElse = (str, defaultVal) => {
	try {
		return JSON.parse(str);
	} catch {
		console.error('Invalid JSON in str');
		return defaultVal;
	}
};

// These both fn also handles, if the value is false , it will return false as value.
export function nullSafeGetWithZero(path: string, obj: any): any {
	const accessKeys = path.split('.');
	return accessKeys.reduce(
		(xs, x) => (xs && xs[x] !== undefined && xs[x] !== null ? xs[x] : undefined),
		obj
	);
}

export function nullSafeGetWithZeroOrElse(path: string, obj: any, elseVal: any): any {
	const accessKeys = path.split('.');
	return accessKeys.reduce(
		(xs, x) => (xs && xs[x] !== undefined && xs[x] !== null ? xs[x] : elseVal),
		obj
	);
}

export function parseQueryParams(queryParams: string): any {
	return queryParams
		.replace('?', '')
		.split('=')
		.reduce((acc, el, idx, arr) => {
			if (idx % 2 !== 0) acc[arr[idx - 1]] = el;
			return acc;
		}, {});
}

export function getEntitiesById(ids, entitiesMap) {
	return ids.map((id) => entitiesMap[id]).filter((_) => !!_);
}

/**
 * `preferSearchRecords` is for lists whose COLUMNS are computed by the search projection and exist
 * nowhere on the SQL record — a refrigerant leak rate, its compliance band, the trailing-12-month
 * pounds. `records[id]` normally wins, so any `/read` that happens to touch the same id (opening the
 * asset picker in a modal, visiting the detail page and navigating back) silently replaces the
 * search doc with a SQL record that has no such fields, and those columns render blank while the
 * SQL-backed ones beside them keep working. Merging rather than replacing keeps hydrated-only
 * associations (nested `location`, `meters`, …) available to the row.
 */
export function getEntitiesByIdWithSearch(ids, slice, preferSearchRecords = false) {
	return ids
		.map((id) => {
			const recordsMap = nullSafeGet('records', slice);
			const searchMap = nullSafeGet('searchRecords', slice);
			const hydratedEntity = recordsMap ? recordsMap[id] : undefined;
			const searchEntity = searchMap ? searchMap[id] : undefined;
			if (!hydratedEntity) {
				return searchEntity;
			}
			if (preferSearchRecords && searchEntity) {
				return { ...hydratedEntity, ...searchEntity };
			}
			// When a row's `/read` response later overwrites `records[id]` (e.g. opening
			// the detail page and navigating back), the server-sent record has no
			// `highlight` field — it's a search-only decoration set by the search
			// adaptor. Re-apply it from `searchRecords[id]` so highlighted sections
			// (Description, Check In Notes, per-channel note highlights, etc.) survive
			// the round-trip.
			if (searchEntity && searchEntity.highlight && !hydratedEntity.highlight) {
				return { ...hydratedEntity, highlight: searchEntity.highlight };
			}
			return hydratedEntity;
		})
		.filter((_) => !!_);
}

/**
 * `fallbackOnMiss` defaults to `{}` because most callers are `mapStateToProps` on a detail page that
 * renders once before its fetch resolves — they read `record.someField` straight off the result, so
 * an empty object keeps that first paint alive where `undefined` would throw.
 *
 * Pass `null` when the caller needs to tell "not loaded" from "found". `{}` is truthy, so it defeats
 * every `if (!record)` / `record && ...` guard, and hands a non-record to anything that reaches more
 * than one level deep (`record.supplierFacility.id` → TypeError). Callers that branch on absence —
 * async selects resolving a pre-selected value, permission gates — want the falsy miss.
 */
export function getEntityById(id, slice, fallbackOnMiss: any = {}) {
	const recordsMap = nullSafeGet('records', slice);
	let entity = recordsMap ? recordsMap[id] : undefined;
	if (!entity) {
		const searchMap = nullSafeGet('searchRecords', slice);
		entity = searchMap && searchMap[id] ? searchMap[id] : fallbackOnMiss;
	}
	return entity;
}

export function getEntitiesByCompositeId(ids, entitiesMap, idNames) {
	return ids.map((id) => entitiesMap[id]);
}

interface PaginationProps {
	current: number;
	pageSize: number;
}

export function getCurrentPage(records: any[], pagination: PaginationProps) {
	const currentPage = pagination.current;
	const pageSize = pagination.pageSize;
	const startIdx = currentPage * pageSize - 1;
	const endIdx = currentPage * pageSize + pageSize;
	return records.slice(startIdx, endIdx);
}

export function chunkify(arr: any[], chunkSize: number, balanced: boolean) {
	if (chunkSize < 2) {
		return [arr];
	}

	let len = arr.length,
		out = [],
		i = 0,
		size;

	if (len % chunkSize === 0) {
		size = Math.floor(len / chunkSize);
		while (i < len) {
			out.push(arr.slice(i, (i += size)));
		}
	} else if (balanced) {
		while (i < len) {
			size = Math.ceil((len - i) / chunkSize--);
			out.push(arr.slice(i, (i += size)));
		}
	} else {
		chunkSize--;
		size = Math.floor(len / chunkSize);
		if (len % size === 0) {
			size--;
		}
		while (i < size * chunkSize) {
			out.push(arr.slice(i, (i += size)));
		}
		out.push(arr.slice(size * chunkSize));
	}
	return out;
}

export function changeFilterValueToArrayOfValues(val: any, separator = ',') {
	return `${val || ''}`.split(separator).filter((_) => !!_);
}

export const changeFilterValueToArrayOfIds = (val: any) =>
	changeFilterValueToArrayOfValues(val).map((_) => parseInt(_));

export function parseFilterValueToAdvancedFilters(val: any) {
	val = val || {};
	return Object.keys(val).reduce((obj, key) => {
		Array.isArray(val[key]) ? (obj[key] = val[key].join(',')) : (obj[key] = val[key]);
		return obj;
	}, {});
}

const queryString = require('qs');

export function getFiltersFromLocation(location) {
	let searchString = location.search;
	if (searchString[0] === '?') {
		searchString = searchString.slice(1);
	}

	return queryString.parse(searchString);
}

export function excludeObject(source, exclude) {
	return Object.keys(source).reduce((obj, key) => {
		source[key] !== exclude[key] && (obj[key] = source[key]);
		return obj;
	}, {});
}

export function isValidFilter(
	filterObj,
	preAppliedFilters = {},
	defaultFilters = {},
	defaultSorter
) {
	const ignoreKeys = [
		...Object.keys(preAppliedFilters),
		...PAGINATION_PROPS_WITHOUT_PAGESIZE,
		...TAB_PROPS,
		'backlinkParams',
	];
	const checkKeys = [...Object.keys(defaultFilters)];
	const sorter = Object.keys(defaultSorter || {}).length > 0 ? defaultSorter : DEFAULT_SORTER;
	const excluded = { ...sorter, ...preAppliedFilters };
	return Object.keys(excludeObject(filterObj, excluded) || {}).some((key) =>
		checkKeys.includes(key) ? filterObj[key] !== defaultFilters[key] : !ignoreKeys.includes(key)
	);
}

export function cleanFiltersForView(filters, preAppliedFilters) {
	const ignoreKeys = [...PAGINATION_PROPS_WITHOUT_PAGESIZE, ...TAB_PROPS];

	return Object.keys(filters).reduce((obj, key) => {
		!ignoreKeys.includes(key) && filters[key] !== undefined && (obj[key] = filters[key]);
		return obj;
	}, {});
}

export const getFiltersWithoutPaginationAndSorting = (params) => {
	const { current, pageSize, sort_by, order, ...filters } = params;
	return filters;
};

export function filterKeys(input, keysArr, withKeys) {
	return Object.keys(input || {}).reduce((obj, key) => {
		withKeys
			? keysArr.includes(key) && (obj[key] = input[key])
			: !keysArr.includes(key) && (obj[key] = input[key]);
		return obj;
	}, {});
}

export function getSearchStringForBackNavigation(location) {
	let searchString = location.search;
	let searchParams = new URLSearchParams(location.search);
	const pathName = location.pathname;
	if (searchString[0] === '?') {
		searchParams.set('pathName', pathName);
		searchString = searchParams.toString();
	} else {
		searchString = `pathName=${pathName}`;
	}
	return searchString;
}

export function getSearchStringForBackNavigationWithBackText(backText, location, extraParams = {}) {
	const searchParams = new URLSearchParams(location.search);
	// Normalize each path segment to fully URL-encoded form so reserved chars
	// in titles (?, &, =, <, >, |, ", etc.) survive round-tripping through the
	// nested backlinkParams query string without bleeding into the path on the
	// way back out (the consumer side runs qs.parse twice).
	const normalizedPathName = location.pathname
		.split('/')
		.map((segment) => {
			if (!segment) return segment;
			let decoded = segment;
			try {
				decoded = decodeURIComponent(segment);
			} catch (_) {
				// invalid percent-encoding — fall back to the raw segment
			}
			return encodeURIComponent(decoded);
		})
		.join('/');
	for (const key in extraParams) {
		searchParams.set(key, extraParams[key]);
	}
	searchParams.set('pathName', normalizedPathName);
	searchParams.set('backLinkText', backText);
	return searchParams.toString();
}
export function getSelectionObject(record) {
	return {
		id: record.id.toString(),
		status: record.status,
		invoicePDFLink: record.invoicePDFLink,
		nte: nullSafeGet('workOrder.nte', record),
		invoiceTotalAfterTax: nullSafeGet('invoiceTotalAfterTax', record),
		invoiceTotalBeforeTax: nullSafeGet('invoiceTotalBeforeTax', record),
		workOrder: nullSafeGet('workOrder', record),
	};
}

export function crushFilters(value: any) {
	const excludeKeys = [...PAGINATION_PROPS];
	const crushed = Object.keys(value || {}).reduce((obj, key) => {
		obj[key] =
			!excludeKeys.includes(key) && value[key] !== undefined && `${value[key]}`
				? JSONCrush.crush(`${value[key]}`)
				: value[key];
		return obj;
	}, {});
	return {
		...crushed,
		...(Object.keys(crushed).length > 0 && { is_compressed: true }),
	};
}

export function parseQueryParameters(queryParams: string): any {
	return queryParams
		.replace('?', '')
		.split(/[=&]/)
		.reduce((acc, el, idx, arr) => {
			if (idx % 2 !== 0) acc[arr[idx - 1]] = el;
			return acc;
		}, {});
}

export const getAllLeafNodes = (records) => {
	let leafNodes = [];
	records.map((record) => {
		if (record.children && record.children.length > 0) {
			leafNodes = [...leafNodes, ...getAllLeafNodes(record.children)];
		} else {
			leafNodes = [...leafNodes, record];
		}
		return undefined;
	});

	return leafNodes;
};

export const findRecord = (allRecords, valueAccessor, val) => {
	const possilbleRecord = allRecords.find((_) => valueAccessor(_) === val);
	if (possilbleRecord) {
		return possilbleRecord;
	}
	for (let i = 0; i < allRecords.length; i++) {
		if (allRecords[i].children && allRecords[i].children.length > 0) {
			const foundRecord = findRecord(allRecords[i].children, valueAccessor, val);
			if (foundRecord) return foundRecord;
		}
	}
	return undefined;
};

export const getFilterFromConfig = (filterConfig, fieldName) =>
	filterConfig.find((_) => _.fieldName === fieldName);

export const isMatchingField = (id, fieldName) => id.startsWith(fieldName);

export const formatDateParameters = (params, dateParamNames = DATE_PARAM_NAMES) =>
	Object.keys(params || {}).reduce((obj, key) => {
		obj[key] =
			dateParamNames.includes(key) && params[key] ? params[key].format('YYYY-MM-DD') : params[key];
		return obj;
	}, {});
// Converts an Eastern time (local) → UTC equivalent
export const adjustToUTCFromEastern = (easternDate) =>
	easternDate
		? moment.tz(easternDate, 'America/New_York').utc().format('MM/DD/YYYY HH:mm:ss')
		: undefined;

// Formats all date fields for URL filters (Eastern → UTC)
export const formatDateForURL = (params, dateParamNames = DATE_PARAM_NAMES) =>
	Object.keys(params || {}).reduce((obj, key) => {
		const val = params[key];
		if (val && dateParamNames.includes(key)) {
			const easternMoment = moment.tz(val, 'America/New_York');

			// Start or end of day in Eastern → converted to UTC
			const adjusted = key.toLowerCase().includes('start')
				? easternMoment.startOf('day')
				: easternMoment.endOf('day');

			obj[key] = adjusted.utc().format('MM/DD/YYYY HH:mm:ss');
		} else {
			obj[key] = val;
		}
		return obj;
	}, {});

export const stopDefaultEvents = (e) => {
	e && e.preventDefault && e.preventDefault();
	e && e.stopPropagation && e.stopPropagation();
	e && e.domEvent && e.domEvent.stopPropagation && e.domEvent.stopPropagation();
};

export const isRangeSelection = () => document.getSelection().type === 'Range';

export const onRowWithRangeSelection = (onRow) => (record) => {
	if (!onRow) return;

	const rowProps = onRow(record);
	if (!rowProps) return; // Check if rowProps is undefined

	const { onClick, ...otherEvents } = onRow(record);
	return {
		...otherEvents,
		onClick: (...args) => {
			if (isRangeSelection()) return;
			onClick && onClick(...args);
		},
	};
};

export const getStartDateFieldName = (fieldName) =>
	fieldName !== 'default' ? `${fieldName}StartDate` : 'startDate';
export const getEndDateFieldName = (fieldName) =>
	fieldName !== 'default' ? `${fieldName}EndDate` : 'endDate';
export const getTimeZoneFieldName = (fieldName) =>
	fieldName !== 'default' ? `${fieldName}TimeZone` : 'timeZone';

export const getTupleNameFieldName = (fieldName) => `${fieldName}Name`;
export const getTupleValueFieldName = (fieldName) => `${fieldName}Value`;

export const isTextElementEmpty = (str) => {
	if (str !== undefined) {
		const el = document.createElement('p');
		el.innerHTML = str;
		str = el.textContent.toString();
		return str.trim().replace(/(<([^>]+)>)/gi, '').length < 1;
	}
	return true;
};

const isDefined = (value) => !!value;

const isExpirationDateValid = (warranty) => {
	const expirationDate = nullSafeGet('expirationDate', warranty);
	return (
		(expirationDate && moment(expirationDate).diff(moment()) > 0) || expirationDate === undefined
	);
};

const isIssueDateValid = (warranty) => {
	const issueDate = nullSafeGet('issueDate', warranty);
	return (issueDate && moment(issueDate).diff(moment()) < 0) || issueDate === undefined;
};

const isSupplierValid = (warranty, supplierId) => {
	return (
		isDefined(supplierId) && supplierId === nullSafeGet('provider.supplierFacilityId', warranty)
	);
};

export const checkIsWarrantyWorkOrder = (warranty, supplierId) => {
	return (
		isDefined(warranty) &&
		isExpirationDateValid(warranty) &&
		isIssueDateValid(warranty) &&
		isSupplierValid(warranty, supplierId)
	);
};

export const getWarrantyStatus = (warranty) =>
	isDefined(warranty)
		? (isDefined(warranty.expirationDate) && moment(warranty.expirationDate).diff(moment()) > 0) ||
		  !isDefined(warranty.expirationDate)
			? //Warranty Expiration Date is in future or is undefined
			  (isDefined(warranty.issueDate) && moment(warranty.issueDate).diff(moment()) < 0) ||
			  (isDefined(warranty.expirationDate) && !isDefined(warranty.issueDate))
				? //Warranty Issue Date is in past or is undefined
				  'Active'
				: //Warranty Issue Date is in future
				  'Pending'
			: isDefined(warranty.expirationDate) &&
			  moment(warranty.expirationDate).add(1, 'months') > moment()
			? //Warranty expiration date was less than a month ago
			  'Expired'
			: false
		: false;

export const checkIsActiveWarrantyFromDates = (startDate, expirationDate) =>
	//expirationDate is less than current date or expiration date is undefined
	(isDefined(expirationDate) ? moment(expirationDate).diff(moment()) > 0 : true) &&
	//issueDate is greater than current date or issue date is undefined
	(isDefined(startDate) ? moment(startDate).diff(moment()) < 0 : true);

export const checkIsWarrantyActive = (warranty) =>
	isDefined(warranty) &&
	checkIsActiveWarrantyFromDates(
		nullSafeGet('issueDate', warranty),
		nullSafeGet('expirationDate', warranty)
	);

export const isLocationWarrantyClaimed = (workOrder) => {
	if (!workOrder.isWarranty) {
		return false;
	}

	const buildingWarrantyStartDate = nullSafeGet('location.buildingWarrantyStartDate', workOrder);
	const buildingWarrantyExpirationDate = nullSafeGet(
		'location.buildingWarrantyExpirationDate',
		workOrder
	);
	const locationWarrantyActive =
		(isDefined(buildingWarrantyStartDate) || isDefined(buildingWarrantyExpirationDate)) &&
		checkIsActiveWarrantyFromDates(buildingWarrantyStartDate, buildingWarrantyExpirationDate);

	if (!locationWarrantyActive) {
		return false;
	}

	const buildingWarrntyProblemTypeIds = nullSafeGetOrElse(
		'location.buildingWarrntyProblemTypeIds',
		workOrder,
		[]
	);
	const problemTypeEligibleForWarranty =
		buildingWarrntyProblemTypeIds.length < 1 ||
		buildingWarrntyProblemTypeIds.includes(workOrder.problemTypeId);

	if (!problemTypeEligibleForWarranty) {
		return false;
	}

	const buildingWarrantyServiceProviderId = nullSafeGet(
		'location.buildingWarrantyServiceProviderId',
		workOrder
	);

	if (!buildingWarrantyServiceProviderId) {
		return true;
	} else {
		const woSupplierFacilityId = nullSafeGet('supplierFacilityId', workOrder);
		if (!woSupplierFacilityId) {
			return true;
		} else {
			return buildingWarrantyServiceProviderId === woSupplierFacilityId;
		}
	}
};

export const hasAccessToAllLocations = (role, user) => {
	const rolesArray: string[] = Array.isArray(role) ? role : role ? [role] : [];
	if (rolesArray.includes('BUYER_ADMIN') || rolesArray.includes('SUPER_ADMIN')) {
		return true;
	}
	return nullSafeGetOrElse('hasAccessToAllLocations', user, false);
};

export const compareStrings = (string1: any, string2: any = ''): boolean => {
	if (typeof string1 !== 'string') string1 = String(string1);
	if (typeof string2 !== 'string') string2 = String(string2);
	return string2.localeCompare(string1, undefined, { sensitivity: 'base' }) === 0;
};

export const isNonEmptyObject = (obj) => {
	if (!obj) return false;

	const cleanedObj = Object.entries(obj)
		.filter(([_, value]) => !!value)
		.reduce((newObj, [key, value]) => {
			newObj[key] = value;
			return newObj;
		}, {});

	return Object.keys(cleanedObj).length > 0;
};
export const equalsIngnoreCase = (str1, str2) =>
	str1 && str2 && `${str1}`.toLowerCase().trim() === `${str2}`.toLowerCase().trim();

export const roundOffToDecimal = (num, places = 2) => {
	const placesNum = Math.pow(10, places);
	return Math.round(num * placesNum) / placesNum;
};

export const checkHasApprovalHierarchy = (item) => {
	return item && item.hasApprovalHierarchy;
};

export const getAnalyticsDisplayData = (data) =>
	data
		.map((el) => ({
			...el,
			subTotal: parseFloat(el.subTotal),
			percentageOfTotal: parseFloat(el.percentageOfTotal),
		}))
		.sort((a, b) => parseFloat(a.subTotal) - parseFloat(b.subTotal));

export const removeEmptyChildrenFromTreeData = (data) =>
	data.map((obj) => ({
		...obj,
		children:
			nullSafeGetOrElse('children.length', obj, 0) > 0
				? removeEmptyChildrenFromTreeData(obj.children)
				: undefined,
	}));

export const flattenTreeData = (data, reduceFn, childPropName = 'children') =>
	data.reduce((acc, obj) => {
		const newAcc = acc.concat([reduceFn({ ...obj })]);
		return newAcc.concat(flattenTreeData(obj[childPropName] || [], reduceFn, childPropName));
	}, []);

export const filterTreeData = (data, filterFn) =>
	data
		.map((obj) => {
			if (filterFn(obj)) {
				return obj;
			} else if (obj.children && obj.children.length > 0) {
				const matchingChildren = filterTreeData(obj.children, filterFn);
				return matchingChildren.length > 0 ? { ...obj, children: matchingChildren } : undefined;
			} else {
				return undefined;
			}
		})
		.filter((_) => !!_);

export const collectObjectsWithValidPropFromTreeData = (data, propName) =>
	data.reduce((acc, obj) => {
		if (nullSafeGetOrElse('children', obj, []).length > 0) {
			return acc.concat(collectObjectsWithValidPropFromTreeData(obj.children, propName));
		} else {
			return acc.concat(obj[propName] ? [obj] : []);
		}
	}, []);

export const collectAllParentsFromTreeData = (data, reducerFn) =>
	data.reduce((acc, obj) => {
		return nullSafeGetOrElse('children', obj, []).length > 0
			? acc.concat(reducerFn(obj)).concat(collectAllParentsFromTreeData(obj.children, reducerFn))
			: acc;
	}, []);

export const sortTreeData = (data, sortFn) =>
	data
		.map((obj) =>
			obj.children
				? {
						...obj,
						children: sortTreeData(obj.children, sortFn),
				  }
				: obj
		)
		.sort(sortFn);

export function createRecordsObjectFromArray(arr, keyName = 'id') {
	return arr.reduce((acc, el) => {
		const key = el[keyName];
		acc[key] = el;
		return acc;
	}, {});
}

export const getCurrency = (props) => {
	const { currentUser, supplier, location, workOrder, buyerCompany, supplierFacility, currencyId } =
		props;

	if (currencyId) {
		return nullSafeGetOrElse(`${currencyId}`, CURRENCIES, 'USD');
	}
	if (nullSafeGet('currencyId', workOrder)) {
		return CURRENCIES[nullSafeGetOrElse('currencyId', workOrder, 'USD')];
	}
	if (nullSafeGet('currencyId', supplierFacility)) {
		return CURRENCIES[nullSafeGetOrElse('currencyId', supplierFacility, 'USD')];
	}
	if (nullSafeGet('currencyId', buyerCompany)) {
		return CURRENCIES[nullSafeGetOrElse('currencyId', buyerCompany, 'USD')];
	}
	if (nullSafeGet('currencyId', supplier)) {
		return CURRENCIES[nullSafeGetOrElse('currencyId', supplier, 'USD')];
	}
	if (nullSafeGet('buyerFacility.currencyId', location)) {
		return CURRENCIES[nullSafeGetOrElse('buyerFacility.currencyId', location, 'USD')];
	}

	if (nullSafeGet('facility.buyerCompany.currencyId', currentUser)) {
		return CURRENCIES[nullSafeGetOrElse('facility.buyerCompany.currencyId', currentUser, 'USD')];
	} else if (nullSafeGetOrElse('facility.currencyId', currentUser, 'USD')) {
		return CURRENCIES[nullSafeGetOrElse('facility.currencyId', currentUser, 'USD')];
	}
};

export const getImageCountErrorRules = (minPhotosCount, message) => [
	...(minPhotosCount
		? [
				{
					required: true,
					message,
				},
		  ]
		: []),
	...(minPhotosCount
		? [
				{
					type: 'array',
					min: minPhotosCount,
					message,
				},
		  ]
		: []),
];
export const fetchWithDifferntSearchName =
	(fetchMethod, searchFieldName) =>
	({ name }, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		fetchMethod(
			{
				[searchFieldName]: name || undefined,
			},
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		);

export const renderCurrency = (currentUser) => (value) => {
	const currency = getCurrency({ currentUser });
	return value ? currency.format(value) : '--';
};

export const renderCurrencyByCurrencyId = (currencyId) => (value) => {
	const currency = getCurrency({ currencyId });
	return value ? currency.format(value) : '--';
};

export const renderCurrencyWithZeroByCurrencyId = (currencyId) => (value) => {
	const currency = getCurrency({ currencyId });
	return isPositiveNumber(value) ? currency.format(value) : '--';
};

const isTruthyEqual = (val1, val2) => !!val1 && !!val2 && val1 === val2;

const getValueFromLocationOrStockLocation = (record, key) =>
	nullSafeGet(key, record) || nullSafeGet(`buyerFacility.${key}`, record);

const getLatitude = (record) => getValueFromLocationOrStockLocation(record, 'latitude');
const getLongitude = (record) => getValueFromLocationOrStockLocation(record, 'longitude');

const isSameLocation = (obj1 = {} as any, obj2 = {} as any) =>
	isTruthyEqual(getLatitude(obj1), getLatitude(obj2)) &&
	isTruthyEqual(getLongitude(obj1), getLongitude(obj2));

export const isSameSelectedLocation = (currentRecord, selectedLocation) => {
	return nullSafeGet('id', selectedLocation) && nullSafeGet('id', currentRecord)
		? currentRecord.id === selectedLocation.id ||
				isSameLocation(currentRecord, selectedLocation) ||
				isSameLocation(currentRecord, nullSafeGet('location', selectedLocation))
		: true;
};

export const getLineItemVendor = (record) =>
	record.isEquipmentLine
		? nullSafeGetOrElse('equipmentCatalog.partEquipmentVendor', record, {})
		: nullSafeGetOrElse('partCatalog.partEquipmentVendor', record, {});

export const isPRLineItemSelectionDisabled = ({
	record,
	selectedLocation,
	selectedSL,
	selectedVendor,
}) => {
	const slToSL = isSameSelectedLocation(nullSafeGet('stockLocation', record), selectedSL);
	const slToLoc = isSameSelectedLocation(nullSafeGet('stockLocation', record), selectedLocation);
	const locToLoc = isSameSelectedLocation(nullSafeGet('location', record), selectedLocation);
	const locToSL = isSameSelectedLocation(nullSafeGet('location', record), selectedSL);
	return (
		record.status !== 'approved' ||
		(nullSafeGet('id', getLineItemVendor(record)) &&
			nullSafeGet('id', selectedVendor) &&
			getLineItemVendor(record).id !== nullSafeGet('id', selectedVendor)) ||
		!slToSL ||
		!slToLoc ||
		!locToLoc ||
		!locToSL
	);
};

export const checkWillChangeApprovalHierarchyQuote = (quote, workOrder, rules) => (param) => {
	const relevantParams = ['isPM', 'isCapex'];

	if (quote && workOrder) {
		let woParams = {
			...relevantParams.reduce((obj, p) => ({ ...obj, [p]: workOrder[p] }), {}),
			...param,
		};
		//Has approval hierarchy
		if (!!nullSafeGet('approvalHierarchy', quote)) {
			const tierDetails = nullSafeGet('approvalHierarchy.tierDetails', quote);
			const hasApprovers = tierDetails.some(
				(tier) => nullSafeGetOrElse('approvedByUsers.length', tier, 0) > 0
			);

			//Has begun approval process
			if (hasApprovers) {
				const currentHierarchyId = nullSafeGet('approvalHierarchyId', quote);
				const defaultRule = rules.find((rule) => rule.isDefault);
				const approvalRules = rules
					//Remove default
					.filter((rule) => !rule.isDefault)
					//Sort Rules by rank
					.sort((a, b) => {
						return a.rank - b.rank;
					})
					//For each check to see if it meets the proper criteria
					.filter((rule) =>
						relevantParams.every((param) => {
							//if rule param is not defined, it is not set and therefore true
							return rule[param] !== undefined ? rule[param] === woParams[param] : true;
						})
					);
				const newRule = approvalRules.length === 0 ? defaultRule : approvalRules[0];
				return currentHierarchyId !== nullSafeGet('approvalHierarchyId', newRule);
			} else {
				return false;
			}
		} else {
			return false;
		}
	} else {
		return false;
	}
};

export const getParamsWithPaginationAndSorting = (params, pagination, sorting, filters) => {
	// if pagination present, transform into limit/offset format and add to query params
	if (pagination) {
		const paginationQuery = {
			offset: (pagination.current - 1) * pagination.pageSize,
			limit: pagination.pageSize,
		};
		params = {
			...params,
			...paginationQuery,
		};
	}

	// if sorting present, fix order naming and add to query params
	if (sorting) {
		const sortingQuery = { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' };
		params = {
			...params,
			...sortingQuery,
		};
	}

	if (filters) {
		params = {
			...params,
			...filters,
		};
	}

	return params;
};

export const isInvoiceTotalGreaterThanNTE = (invoice, workOrder) => {
	// NTE is a work-order concept. Without one there is nothing to exceed, and the 0
	// fallback below would otherwise read as "over NTE" for any positive total — which
	// is what disabled Approve on every project invoice, blocked publishing a draft one,
	// and showed a warning citing a work order NTE that does not exist.
	//
	// Keyed on a real id rather than object truthiness: InvoiceActions passes
	// getSanitisedWorkOrder(invoice.workOrder), which spreads `undefined` into an object
	// of undefined fields — truthy, so a plain `!workOrder` check sailed straight past it.
	if (!nullSafeGet('id', workOrder)) {
		return false;
	}
	const nte = parseFloat(nullSafeGetOrElse('nte', workOrder, 0));
	const total = parseFloat(nullSafeGetOrElse('invoiceTotalAfterTax', invoice, 0));
	const totalBeforeTax = parseFloat(nullSafeGetOrElse('invoiceTotalBeforeTax', invoice, 0));
	const isNteCheckWithoutTax = nullSafeGet(
		'buyerCompany.buyerCompanySettings.config.invoiceConfig.nteCheckWithoutTax',
		workOrder
	);

	return isNteCheckWithoutTax ? nte < totalBeforeTax : nte < total;
};

export const getBudgetPriorityValue = (budget) => {
	return budget.invoiceTotal || budget.proposalTotal || budget.workOrderNte || 0;
};

export const getTotalValue = (v1, v2) => {
	if (v1 !== undefined && v2 !== undefined) {
		return v1 + v2;
	} else if (v1 !== undefined) {
		return v1;
	} else if (v2 !== undefined) {
		return v2;
	}

	return undefined;
};

export const getIfAllSameCurrencies = (currencies) =>
	currencies.length > 0
		? currencies.every((_) => _ === currencies[0])
			? currencies[0]
			: undefined
		: undefined;

export const getCurrencyIdIfSameLineItemsCurrency = (lineItems, prefix) => {
	const currencies = lineItems
		.map((_) => nullSafeGet(`${prefix}Catalog.partEquipmentVendor.currencyId`, _))
		.filter((_) => !!_);
	return getIfAllSameCurrencies(currencies);
};

export const getCurrencyIdIFSamePRLineItemCurrency = (purchaseRequest) => {
	const partCurrency = getCurrencyIdIfSameLineItemsCurrency(
		nullSafeGetOrElse('partSupplierPurchaseRequestLineItems', purchaseRequest, []),
		'part'
	);
	const equipmentCurrency = getCurrencyIdIfSameLineItemsCurrency(
		nullSafeGetOrElse('equipmentSupplierPurchaseRequestLineItems', purchaseRequest, []),
		'equipment'
	);
	return getIfAllSameCurrencies([partCurrency, equipmentCurrency].filter((_) => !!_));
};

export const mergeTroubleshootingRoutes = (sourceArray, toBeMerged, keyFn, reducerFn) => {
	return toBeMerged.reduce((keys, route) => {
		const defaultStepIndex = keyFn(route);
		return [
			...keys.slice(0, defaultStepIndex + 1),
			...[reducerFn(route)],
			...keys.slice(defaultStepIndex + 1),
		];
	}, sourceArray);
};

export const getPurchaseOrderId = (po) =>
	`#${nullSafeGetOrElse(
		'purchaseOrderUniqueIdWithPrefix',
		po,
		`${nullSafeGetOrElse('id', po, '--')}`
	)}`;

export const getPurchaseRequestId = (pr) =>
	`#${nullSafeGetOrElse(
		'purchaseRequestCustomIdentifier',
		pr,
		`${nullSafeGetOrElse('id', pr, '--')}`
	)}`;

export const getEquipmentUniqueIdorElseId = (epsl) =>
	nullSafeGetOrElse('equipmentUniqueId', epsl, nullSafeGetOrElse('id', epsl, ''));

export const isToAssetPOLineItem = (record) =>
	nullSafeGet('equipmentTypeId', record) && nullSafeGet('supplierPurchaseOrder.locationId', record);

export const getElementAt = (arr, index) => (index >= 0 ? arr[index] : arr[arr.length + index]);

export const isAdminUser = (currentUser) =>
	nullSafeGetOrElse('roles', currentUser, []).some(
		(role) => BUYER_ADMIN_ONLY.has(role) || SUPPLIER_ADMIN_ONLY.has(role)
	);
export const isBuyerAdminUser = (currentUser) =>
	nullSafeGetOrElse('roles', currentUser, []).some((role) => BUYER_ADMIN_ONLY.has(role));

export const isSupplierPayingCustomer = (currentUser) =>
	currentUser.userType === ROLE_TYPES.SUPPLIER &&
	nullSafeGet('facility.supplierCompany.isPayingSupplier', currentUser);

export const isSupplierPayingCustomerAndAdmin = (currentUser) =>
	isSupplierPayingCustomer(currentUser) && isAdminUser(currentUser);

export const canAccessInternalWorkspace = (currentUser): boolean =>
	isSupplierPayingCustomer(currentUser) || isInternalTech(currentUser);

// Gates the Pro features for adding work-order labels and starring with a note.
// Buyers and internal techs always have access; suppliers only when on a paying plan.
export const canAddWOLabelsOrStarNotes = (currentUser): boolean =>
	nullSafeGet('userType', currentUser) === ROLE_TYPES.BUYER ||
	isSupplierPayingCustomer(currentUser) ||
	isInternalTech(currentUser);

export const isSubContractor = (currentUser, workOrder) =>
	currentUser.userType === ROLE_TYPES.SUPPLIER &&
	nullSafeGet('facility.id', currentUser) ===
		nullSafeGet('subContractedFromWorkOrder.subSupplierFacilityId', workOrder);

export const isContractor = (currentUser, contractorWO) =>
	currentUser.userType === ROLE_TYPES.SUPPLIER &&
	nullSafeGet('facility.id', currentUser) ===
		nullSafeGet('contractorSupplierFacilityId', contractorWO);

// Mirrors WorkOrderUtil.hasSupplierWritePermission on the backend. A sub-supplier on a
// sub-contracted work order gets read access to the root work order but not write, so the root
// row is not theirs to edit — their editable surface is the ContractorWorkOrder.
export const hasSupplierWriteAccessToWorkOrder = (currentUser, workOrder) => {
	const facilityId = nullSafeGet('facility.id', currentUser);
	if (!facilityId) return false;
	if (facilityId === nullSafeGet('supplierFacilityId', workOrder)) return true;

	// A sibling facility of the assigned supplier's company may write it too. Both ids have to
	// resolve before that can be concluded — `supplierFacility` is not hydrated everywhere.
	const userCompanyId =
		nullSafeGet('facility.supplierCompanyId', currentUser) ||
		nullSafeGet('facility.supplierCompany.id', currentUser);
	const workOrderCompanyId = nullSafeGet('supplierFacility.supplierCompanyId', workOrder);
	if (userCompanyId && workOrderCompanyId && userCompanyId === workOrderCompanyId) return true;

	return nullSafeGetOrElse('visibilityReadWriteSupplierFacilityIdsString', workOrder, '').includes(
		`,${facilityId},`
	);
};

export const getServiceCallConfigValueBasedOnSupplierOrInternalTeam = (
	workOrder,
	currentUser,
	configName,
	defaultValue
) => {
	return nullSafeGet('userType', currentUser) === ROLE_TYPES.BUYER
		? defaultValue
		: isInternalTech(currentUser)
		? nullSafeGetOrElse(`facility.internalTechConfig.${configName}`, currentUser, defaultValue)
		: nullSafeGetOrElse(
				`buyerCompany.buyerCompanySettings.config.suppliersConfig.${configName}`,
				workOrder,
				defaultValue
		  );
};

export const isCheckInModalMandatory = (workOrder, currentUser) => {
	const numImagesRequired = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
		workOrder,
		currentUser,
		'numImagesRequiredOnCheckIn',
		0
	);
	const checkInNotesMandatory = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
		workOrder,
		currentUser,
		'checkInNotesMandatory',
		false
	);

	return checkInNotesMandatory || numImagesRequired > 0;
};

export const getConfigValueDefaultingToTrue = (companyConfig, configNamePath, propName) => {
	const config = nullSafeGetOrElse(configNamePath, companyConfig, {});
	return typeof config[propName] === 'undefined' ? true : config[propName];
};

export const getPONumbers = (workOrder) =>
	String(nullSafeGetOrElse('poNumber', workOrder, ''))
		.split(',')
		.filter((_) => !!_)
		.join(', ');

export const isPositiveNumber = (value) => value !== null && value !== undefined && value >= 0;

export const getDisabledDate =
	(disabledBefore = undefined, tz = defaultTimeZone, disabledAfter = undefined) =>
	(current) => {
		if (!current) return false;

		const dayStartUtc = moment(wallTimeToUtcIso(current.clone().startOf('day'), tz)).utc();
		const dayEndUtc = moment(wallTimeToUtcIso(current.clone().endOf('day'), tz)).utc();

		const minUtc = disabledBefore ? moment(disabledBefore).utc() : undefined;
		const maxUtc = disabledAfter ? moment(disabledAfter).utc() : undefined;

		// disable if the entire day is before min or after max
		return (
			(disabledBefore && dayEndUtc.isBefore(minUtc)) ||
			(disabledAfter && dayStartUtc.isAfter(maxUtc))
		);
	};

export const getDisabledDateTime =
	(
		disabledBeforeIso: string | undefined = undefined,
		tz: string = defaultTimeZone,
		disabledAfterIso: string | undefined = undefined,
		minuteIncrement: number = 0
	) =>
	(current) => {
		if (!current) return {};

		const hasMin = !!disabledBeforeIso;
		const minUtc = hasMin ? moment(disabledBeforeIso).utc() : undefined;

		const hasMax = !!disabledAfterIso;
		const rawMaxUtc = hasMax ? moment(disabledAfterIso!).utc() : undefined;

		const safeIncrement = minuteIncrement && minuteIncrement > 0 ? minuteIncrement : 1;

		// If max provided and min provided, ensure window isn't empty (at least one increment wide)
		const maxUtc =
			hasMax && rawMaxUtc
				? hasMin && minUtc
					? moment.max(rawMaxUtc, minUtc.clone().add(safeIncrement, 'minutes'))
					: rawMaxUtc
				: undefined;

		const dateStr = current.format('YYYY-MM-DD');

		const candidateUtc = (h: number, m: number, s: number) =>
			moment
				.tz(
					`${dateStr} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
						s
					).padStart(2, '0')}`,
					'YYYY-MM-DD HH:mm:ss',
					tz
				)
				.utc();

		const isAllowedUtc = (mUtc: moment.Moment) => {
			if (hasMin && minUtc && mUtc.isBefore(minUtc)) return false;
			if (hasMax && maxUtc && mUtc.isAfter(maxUtc)) return false;
			return true;
		};

		// First valid slot of the day in tz, rounded up to increment
		const firstValidSlotForDayInTz = () => {
			const selectedDayStartInTz = moment.tz(`${dateStr} 00:00:00`, 'YYYY-MM-DD HH:mm:ss', tz);

			// If no min, base is start of selected day
			const base = hasMin && minUtc ? minUtc.clone().tz(tz) : selectedDayStartInTz;

			// If min exists but is not on the selected day in tz, use start of selected day
			const baseForDay =
				hasMin && minUtc && base.isSame(selectedDayStartInTz, 'day') ? base : selectedDayStartInTz;

			// Round UP to next increment
			const roundedMinute = Math.ceil(baseForDay.minute() / safeIncrement) * safeIncrement;

			const slot = baseForDay.clone().second(0).millisecond(0);
			if (roundedMinute >= 60) {
				slot.add(1, 'hour').minute(0);
			} else {
				slot.minute(roundedMinute);
			}

			return slot;
		};

		const disabledHours = () => {
			const hours: number[] = [];
			for (let h = 0; h < 24; h++) {
				const startUtc = candidateUtc(h, 0, 0);
				const endUtc = candidateUtc(h, 59, 59);

				const hourAllBefore = hasMin && minUtc ? endUtc.isBefore(minUtc) : false;
				const hourAllAfter = hasMax && maxUtc ? startUtc.isAfter(maxUtc) : false;

				if (hourAllBefore || hourAllAfter) hours.push(h);
			}
			return hours;
		};

		const disabledMinutes = (selectedHour: number) => {
			const mins: number[] = [];
			for (let m = 0; m < 60; m++) {
				const utc = candidateUtc(selectedHour, m, 0);
				if (!isAllowedUtc(utc)) mins.push(m);
			}

			// Rescue: if all minutes disabled, enable the increment bucket containing the first valid slot
			if (mins.length === 60 && safeIncrement > 1) {
				// Only rescue inside this hour
				const slotInTz = firstValidSlotForDayInTz();

				if (slotInTz.hour() === selectedHour) {
					const start = slotInTz.minute();
					const enabled: number[] = [];

					for (let i = 0; i < safeIncrement; i++) {
						const mm = start + i;
						if (mm >= 60) break;

						const utc = candidateUtc(selectedHour, mm, 0);
						if (isAllowedUtc(utc)) enabled.push(mm);
					}

					if (enabled.length) {
						return mins.filter((m) => !enabled.includes(m));
					}
				}
			}

			return mins;
		};

		const disabledSeconds = (selectedHour: number, selectedMinute: number) => {
			const secs: number[] = [];
			for (let s = 0; s < 60; s++) {
				const utc = candidateUtc(selectedHour, selectedMinute, s);
				if (!isAllowedUtc(utc)) secs.push(s);
			}

			// If all seconds disabled, allow second 0 if that instant is allowed
			if (secs.length === 60) {
				const utc0 = candidateUtc(selectedHour, selectedMinute, 0);
				if (isAllowedUtc(utc0)) return secs.filter((s) => s !== 0);
			}

			return secs;
		};

		return { disabledHours, disabledMinutes, disabledSeconds };
	};

export const getStateSliceProp = (propName, targetCollectionName, stateSlice) =>
	nullSafeGetOrElse(`${targetCollectionName}.${propName}`, stateSlice, {});

export const mergeTypeFieldsInit = (currentTypeDetails, typeDetails) => {
	const fieldsToMoveToOptional = currentTypeDetails.filter(
		(field) => !typeDetails.requiredFields.some((requiredField) => requiredField.key === field.key)
	);
	const mergedOptionalFields = [...typeDetails.optionalFields, ...fieldsToMoveToOptional];

	// Ensure uniqueness
	const uniqueOptionalFields = mergedOptionalFields.reduce((acc, field) => {
		if (!acc.some((f) => f.key === field.key) && field.key !== '') {
			acc.push(field);
		}
		return acc;
	}, []);

	return {
		...typeDetails,
		requiredFields: typeDetails.requiredFields,
		optionalFields: uniqueOptionalFields,
	};
};

export const mergeTypeFields = (currentType, newType) => {
	const previousOptionalFields = currentType.optionalFields || [];
	const previousRequiredFields = currentType.requiredFields || [];

	const isOldOptionalOrRequiredField = (f) => {
		return (
			!newType.requiredFields.some((newRequiredField) => newRequiredField.key === f.key) &&
			!newType.optionalFields.some((newOptionalField) => newOptionalField.key === f.key)
		);
	};

	// Fields to move from previous required to optional (if not required anymore)
	const fieldsToMoveToOptional = previousRequiredFields.filter(
		(requiredField) =>
			!newType.requiredFields.some((newRequiredField) => newRequiredField.key === requiredField.key)
	);

	// Separate fields that need to be moved from optional to required
	const fieldsToMoveToRequired = previousOptionalFields.filter((optionalField) =>
		newType.requiredFields.some((requiredField) => requiredField.key === optionalField.key)
	);

	// Remove these fields from the previous optional fields
	const remainingOptionalFields = previousOptionalFields.filter(
		(optionalField) => !fieldsToMoveToRequired.some((field) => field.key === optionalField.key)
	);

	// Combine remaining optional fields with the new optional fields
	const updatedOptionalFields = [
		...remainingOptionalFields,
		...fieldsToMoveToOptional,
		...newType.optionalFields,
	];

	// Ensure uniqueness in optional fields
	const uniqueOptionalFields = updatedOptionalFields.reduce((acc, field) => {
		if (!acc.some((f) => f.key === field.key) && field.key !== '') {
			acc.push(field);
		}
		return acc;
	}, []);

	// Return updated type with new required and optional fields
	return {
		...newType,
		requiredFields: newType.requiredFields.map((f) => ({
			...f,
			remove: isOldOptionalOrRequiredField(f),
		})),
		optionalFields: uniqueOptionalFields.map((f) => ({
			...f,
			remove: isOldOptionalOrRequiredField(f),
		})),
	};
};

// this util wasn't available in the codebase. it is needed in multiple places so added here.
export const parseNumber = (value): number | null => {
	if (value === null || value === undefined || value === '') return null;
	const parsed = Number(value);
	return isNaN(parsed) ? null : parsed;
};

export const percentageDifference = (currentVal, previousVal) => {
	if (currentVal && previousVal) {
		return (currentVal - previousVal) / ((currentVal + previousVal) / 2);
	} else {
		return null;
	}
};
export const contactObjectExists = (contact) => !!nullSafeGet('email', contact);

export const disabledByAnotherAction = (loadingState, ownStatus) =>
	!!loadingState && loadingState !== ownStatus;

export const uuidv4 = () =>
	'10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
		(+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
	);

export const isHierarchyNTEBased = (tiers) => {
	return !tiers.every((tier) => tier.amountUpto === undefined);
};

export const highestTierSplitPartiallyApproved = (tiers, highestApprovedAmount) =>
	[...tiers]
		.filter((tier) => {
			return (tier.amountUpto ? parseInt(tier.amountUpto) : Infinity) === highestApprovedAmount;
		})
		.some((tier) => tier.approvers.length !== 0 && tier.approvedByUsers.length === 0);

export const nullSafeToFixed = (value, digits = 2) =>
	value && !isNaN(value) ? value.toFixed(digits) : null;

export const removeHydratedProps = (eneity = {}) =>
	hydratedPropNames.reduce(
		(acc, propName) => ({
			...acc,
			[propName]: undefined,
		}),
		eneity
	);

export const isValidNumber = (value) => {
	return !isNaN(value) && typeof value === 'number';
};

export const isFiniteNumber = (value): boolean =>
	typeof value === 'number' && !isNaN(value) && isFinite(value);

export const sortById = (items) =>
	[...items].sort((a, b) => {
		const allIds = items.filter((_) => !!_.id).map((_) => _.id);
		const max = Math.max(...allIds);
		return (b.id || max + 1) - (a.id || max + 1);
	});

export const stripEmTags = (html: string): string => html.replace(/<\/?em>/g, '');

export const extractTextContent = (htmlString) => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlString, 'text/html'); // Parse the string
	return doc.body.textContent; // Extract the text content
};

export const getInternalTechConfig = (currentUser) =>
	currentUser && currentUser.facility && currentUser.facility.internalTechConfig;

export const moveObjectToBeginning = (arr, key, value) => {
	const newArr = [...arr];
	const index = newArr.findIndex((obj) => obj[key] === value);
	if (index > -1) {
		const [item] = newArr.splice(index, 1); // Remove the found object
		newArr.unshift(item); // Insert it at the beginning
	}
	return newArr;
};

export const isNotUndefined = (value) => {
	return typeof value !== 'undefined' && value !== null;
};

export const emailValidator = (rule, values, callback) => {
	if (!values) {
		callback();
	}

	if (Array.isArray(values)) {
		const invalidInputs = values && values.filter((value) => !value.match(emailRegex));
		if (invalidInputs.length === 0 || values === undefined) {
			callback();
		} else if (invalidInputs.length === 1) {
			callback(invalidInputs.join('') + ' is not a valid email');
		} else {
			callback(
				invalidInputs.slice(0, -1).join(', ') +
					' and ' +
					invalidInputs.slice(-1) +
					' are not valid emails'
			);
		}
	} else if (typeof values === 'string') {
		if (values.match(emailRegex)) {
			callback();
		} else {
			callback(values + ' is not a valid email');
		}
	}
};

export const businessTypeOptions = [
	{ key: 'restaurant', value: 'Restaurant' },
	{ key: 'retail', value: 'Retail' },
	{ key: 'fitness', value: 'Fitness' },
	{ key: 'clinics', value: 'Clinics' },
	{ key: 'otherLocations', value: 'Other brick and mortar locations' },
	{ key: 'brokers', value: 'Broker/Contractors' },
];

export const businessTypeMap = businessTypeOptions.reduce((acc, { key, value }) => {
	acc[key] = value;
	return acc;
}, {});

export const getPicturesFromFormDataForUpload = (photosList = []) =>
	photosList
		.map((p) => {
			const fileName = nullSafeGetOrElse('response.data.fileName', p, '');
			const fileId = nullSafeGetOrElse('response.data.fileId', p, '');
			return fileName && fileId ? `${fileId}/${fileName}` : undefined;
		})
		.filter((_) => !!_);

export const getWalkthroughScoreObject = (walkthrough, groupTitle = 'Overall Score') =>
	nullSafeGetOrElse('scores', walkthrough, []).find((score) => score.groupTitle === groupTitle);

export const getPrimaryColor = (userType) => {
	return compareStrings(userType, ROLE_TYPES.SUPPLIER) ? '#266F83' : '#53298e';
};

export const getLowestBidderSupplierFacilityId = (bidders) => {
	const lowestBiddder = (bidders || [])
		.map((bidder) => ({
			...bidder,
			total: Number(nullSafeGetOrElse('proposal.totalAfterTax', bidder, 0)),
		}))
		.filter((bidder) => bidder.total !== 0)
		.reduce((min, curr) => (curr.total < min.total ? curr : min), { total: Infinity });
	return nullSafeGetOrElse('supplierFacilityId', lowestBiddder, -1);
};

export const valueCompareWithoutType = (v1, v2) => `${v1}` === `${v2}`;

export const isInternalTeam = (supplier) => nullSafeGet('status', supplier) === 'private';

export const recordHasContractorId = (record) => record.contractorWorkOrderId !== undefined;

export const getCategoryNamesDisplay = (categoryNamesString) => {
	const categoryNamesArray = (categoryNamesString || '').split(',');
	return `${categoryNamesArray
		.map((categoryName) => capitalizeEachWord(categoryName))
		.slice(0, 2)
		.join(', ')}${categoryNamesArray.length > 3 ? ' and more' : ''}`;
};

const BASE62_CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const encodeNumberToBase62 = (num) => {
	if (num === 0) return '0';
	let result = '';
	while (num > 0) {
		result = BASE62_CHARS[num % 62] + result;
		num = Math.floor(num / 62);
	}
	return result;
};

export const decodeBase62ToNumber = (str) => {
	return str
		.split('')
		.reverse()
		.reduce((acc, char, i) => {
			return acc + BASE62_CHARS.indexOf(char) * 62 ** i;
		}, 0);
};

const getKeyStringFromArrayBuffer = (ab) =>
	ab ? btoa(String.fromCharCode.apply(null, new Uint8Array(ab))) : undefined;

export const getSubscriptionPayload = (subscription: PushSubscription) => ({
	endpoint: nullSafeGet('endpoint', subscription),
	p256dh: getKeyStringFromArrayBuffer(subscription.getKey('p256dh')),
	auth: getKeyStringFromArrayBuffer(subscription.getKey('auth')),
});

export const getPropsFromObjectForHeaderKeys = (record, headerDefs) =>
	headerDefs.reduce((acc, h) => {
		acc[h.key] = record[h.key] !== undefined && record[h.key] !== null ? record[h.key] : '';
		return acc;
	}, {});

export const sortItemsWithLockedAtEnd = (items, isManagedBySupplier) => {
	return items.sort((a, b) => {
		const aLocked = a.lockedIfManagedBySupplier && isManagedBySupplier;
		const bLocked = b.lockedIfManagedBySupplier && isManagedBySupplier;
		if (aLocked && !bLocked) return 1;
		if (!aLocked && bLocked) return -1;
		return 0;
	});
};

export const getReviewTagsWithCount = (record) => {
	return nullSafeGetOrElse('reviewTagsWithCount', record, [])
		.filter((t) => t.isPositiveSentiment)
		.map((tag) => ({
			...tag,
			count: nullSafeGet('count', tag),
			reviewTag: {
				...tag,
				isPositiveSentiment: nullSafeGet('isPositiveSentiment', tag),
				title: nullSafeGet('title', tag),
			},
		}));
};

export const createRawAddress = (addr) => {
	if (!addr || addr === undefined) {
		return '';
	}
	return `${addr.streetAddress1}  ${addr.city}, ${addr.region}, ${addr.postcode}, ${addr.country}`;
};

export const sanitizeCustomFieldName = (name) => name.replace(/[^a-zA-Z0-9]/g, '_');

export const getCustomFieldsForAPI = (values, customFields) => {
	const customFieldDetails = (customFields || []).map((field) => ({
		key: values[`custom-${sanitizeCustomFieldName(field.fieldName)}-fieldName`],
		valueType: values[`custom-${sanitizeCustomFieldName(field.fieldName)}-valueType`],
		valueAsString: values[`custom-${sanitizeCustomFieldName(field.fieldName)}-valueAsString`],
	}));
	return customFieldDetails;
};

export const getLabelFromArrayOfKeys = (arrayValues, key, keyName, labelName) =>
	nullSafeGetOrElse(
		labelName,
		arrayValues.find((_) => _[keyName] === key),
		key
	);

export const getStatusLabelFromPipelineFilters = (pipeLineFilters, status) =>
	getLabelFromArrayOfKeys(pipeLineFilters, status, 'name', 'displayName');

export const getUnreadNotesIndexAndCount = (notes, currentUser) => {
	// Only bail on an empty list — a single (possibly grouped) unread row must still
	// surface its count/separator rather than being zeroed by a `< 2` guard.
	if (notes.length < 1) {
		return {
			firstUnreadMessageIndex: -1,
			unreadMessageLength: 0,
		};
	}

	const firstUnreadMessageIndex = notes.findIndex((n) => {
		const noteReadDetails = nullSafeGetOrElse('noteReadDetails', n, []);
		return (
			n.type !== NOTE_TYPE_LOG &&
			n.type !== NOTE_TYPE_INTERNAL_ATTACHMENT_LOG &&
			n.type !== NOTE_TYPE_SERVICE_CALL_CHECK_IN &&
			n.type !== NOTE_TYPE_SERVICE_CALL_CHECK_OUT &&
			!compareStrings(n.noteAddedBy, currentUser.email) &&
			!noteReadDetails.find((_) => compareStrings(_.readBy, currentUser.email))
		);
	});
	const unreadMessageLength = notes.length - (firstUnreadMessageIndex || 0);

	return {
		firstUnreadMessageIndex,
		unreadMessageLength,
	};
};

export const getWorkOrderTimeZoneId = (workOrder = {}) =>
	nullSafeGetOrElse(
		'location.timeZoneId',
		workOrder,
		nullSafeGetOrElse(
			'subContractedFromWorkOrder.contractorTenant.timeZoneId',
			workOrder,
			nullSafeGetOrElse(
				'subContractedToWorkOrder.contractorTenant.timeZoneId',
				workOrder,
				defaultTimeZone
			)
		)
	);

// Convert array values to comma-separated strings to match the format
// the backend expects (same as how GET query params serialize arrays)
export const flattenArrayValues = (obj: Record<string, any>): Record<string, any> => {
	if (!obj) return obj;
	return Object.keys(obj).reduce((acc, key) => {
		acc[key] = Array.isArray(obj[key]) ? obj[key].join(',') : obj[key];
		return acc;
	}, {} as Record<string, any>);
};
